import { useSWRConfig } from 'swr';
import FeedContentType from '@/constants/FeedContentType';
import translate from '@/lib/intl/translate';
import { createApiRequest } from '@/utils/services';
import showErrorMessage from '@/utils/toasts/showErrorMessage';
import showSuccessMessage from '@/utils/toasts/showSuccessMessage';
import useEntity from './useEntity';
import './_typedef';

/**
 * Fetches data for a donation by ID.
 * @param {string?} id
 * @param {object?} swrOptions
 * @returns {SwrEntity}
 */
const useContribution = (id, swrOptions = {}) => {
  const { mutate: globalMutate } = useSWRConfig();
  const { mutate, remove, ...rest } = useEntity(
    id,
    FeedContentType.DONATION,
    swrOptions,
  );

  const deleteContribution = async (showConfirmation = true) => {
    try {
      await remove();
      if (showConfirmation) {
        showSuccessMessage({
          message: translate('CONTRIBUTION_DELETE_SUCCESS'),
        });
      }
    } catch (err) {
      if (showConfirmation) {
        showErrorMessage({
          message: translate('CONTRIBUTION_DELETE_FAILURE'),
        });
      } else {
        throw err;
      }
    }
  };

  const mutateContribution = async (update, showConfirmation = true) => {
    try {
      /**
       * When PUTing a donation, it accepts "notes" as a single string instead
       * of an array of text we get from GET donation. This can cause weird
       * prop errors when going from array to string using the traditional way
       * of mutating a donation. To get around that, we use the "global" mutate
       * and modify the notes from a string to an array of strings. This makes
       * the prop type gods happy.
       */
      await globalMutate(`donations/${id}`, async () => {
        const res = await createApiRequest({
          data: update,
          method: 'put',
          path: `donations/${id}`,
          throwErrors: true,
        });

        return {
          ...res.data.donation,
          ...update,
          notes: [
            {
              ...(update.notes && { text: update.notes }),
            },
          ],
        };
      });
      if (showConfirmation) {
        showSuccessMessage({
          message: translate('CONTRIBUTION_UPDATE_SUCCESS'),
        });
      }
    } catch (error) {
      showErrorMessage({
        message: translate('CONTRIBUTION_UPDATE_FAILURE'),
      });
    }
  };

  return {
    remove: deleteContribution,
    mutate: mutateContribution,
    ...rest,
  };
};

export default useContribution;
